/**
 * Google Places API Service
 * Uses Google Maps JavaScript SDK for address autocomplete
 * No CORS issues - SDK handles all API communication
 */

export interface GoogleAddressSuggestion {
  id: string;
  label: string;
  placeId: string;
  description: string;
}

export interface GoogleAddressDetails {
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  locality: string;
  postcode: string;
  state: string;
}

export interface GoogleLocationSuggestion extends GoogleAddressSuggestion {
  value: string;
  mainText: string;
  secondaryText: string;
}

// Declare Google Maps types
declare global {
  interface Window {
    google: typeof google;
  }
}

/**
 * Wait for Google Maps API to be loaded
 */
function waitForGoogleMaps(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window.google !== 'undefined' && window.google.maps) {
      resolve();
      return;
    }

    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait
    const interval = setInterval(() => {
      attempts++;
      if (typeof window.google !== 'undefined' && window.google.maps) {
        clearInterval(interval);
        resolve();
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        reject(new Error('Google Maps API failed to load'));
      }
    }, 100);
  });
}

/**
 * Get address suggestions from Google Places Autocomplete using JavaScript SDK
 */
export async function getGoogleAddressSuggestions(
  searchText: string
): Promise<GoogleAddressSuggestion[]> {
  if (searchText.trim().length < 2) {
    return [];
  }

  try {
    // Wait for Google Maps API to be ready
    await waitForGoogleMaps();

    console.log('Fetching suggestions for:', searchText);

    // Create AutocompleteService instance
    const autocompleteService = new google.maps.places.AutocompleteService();

    // Get predictions
    const request: google.maps.places.AutocompletionRequest = {
      input: searchText,
      componentRestrictions: { country: 'au' }, // Restrict to Australia
      types: ['address'], // Only return addresses
    };

    return new Promise((resolve, reject) => {
      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        console.log('Google Places response status:', status);

        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          console.log('Found', predictions.length, 'suggestions');

          const suggestions: GoogleAddressSuggestion[] = predictions.map((prediction) => ({
            id: prediction.place_id,
            label: prediction.description,
            placeId: prediction.place_id,
            description: prediction.description,
          }));

          resolve(suggestions);
        } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          console.log('No predictions found');
          resolve([]);
        } else {
          console.error('Google Places API returned status:', status);
          reject(new Error(`Google Places API error: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error('Google Places autocomplete error:', error);
    throw error;
  }
}

/**
 * Get suburb/postcode style location suggestions from Google Places Autocomplete.
 */
export async function getGoogleLocationSuggestions(
  searchText: string
): Promise<GoogleLocationSuggestion[]> {
  if (searchText.trim().length < 2) {
    return [];
  }

  try {
    await waitForGoogleMaps();

    const autocompleteService = new google.maps.places.AutocompleteService();
    const request: google.maps.places.AutocompletionRequest = {
      input: searchText,
      componentRestrictions: { country: 'au' },
      types: ['(regions)'],
    };

    return new Promise((resolve, reject) => {
      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          resolve(
            predictions.map(prediction => ({
              id: prediction.place_id,
              label: prediction.description.replace(/,\s*Australia$/i, ''),
              value: prediction.description,
              placeId: prediction.place_id,
              description: prediction.description,
              mainText:
                prediction.structured_formatting?.main_text ||
                prediction.description.split(',')[0],
              secondaryText:
                prediction.structured_formatting?.secondary_text
                  ?.replace(/,\s*Australia$/i, '') || '',
            }))
          );
        } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          reject(new Error(`Google Places API error: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error('Google Places location autocomplete error:', error);
    throw error;
  }
}

/**
 * Get detailed information about a place using its place_id
 */
export async function getGooglePlaceDetails(placeId: string): Promise<GoogleAddressDetails | null> {
  try {
    // Wait for Google Maps API to be ready
    await waitForGoogleMaps();

    console.log('Fetching place details for:', placeId);

    // Create a temporary div for PlacesService (required by Google API)
    const div = document.createElement('div');
    const placesService = new google.maps.places.PlacesService(div);

    const request: google.maps.places.PlaceDetailsRequest = {
      placeId: placeId,
      fields: ['formatted_address', 'geometry', 'address_components'],
    };

    return new Promise((resolve, reject) => {
      placesService.getDetails(request, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          console.log('Successfully fetched place details');

          // Extract address components
          let locality = '';
          let postcode = '';
          let state = '';

          place.address_components?.forEach((component) => {
            if (component.types.includes('locality')) {
              locality = component.long_name;
            } else if (component.types.includes('postal_code')) {
              postcode = component.long_name;
            } else if (component.types.includes('administrative_area_level_1')) {
              state = component.short_name;
            }
          });

          const details: GoogleAddressDetails = {
            address: place.formatted_address || '',
            coordinates: {
              lat: place.geometry?.location?.lat() || 0,
              lng: place.geometry?.location?.lng() || 0,
            },
            locality,
            postcode,
            state,
          };

          resolve(details);
        } else {
          console.error('Google Place Details API returned status:', status);
          reject(new Error(`Place details error: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error('Google Place Details error:', error);
    return null;
  }
}

/**
 * Geocode an address string to coordinates
 * Fallback option if place_id isn't available
 */
export async function geocodeAddress(address: string): Promise<GoogleAddressDetails | null> {
  try {
    // Wait for Google Maps API to be ready
    await waitForGoogleMaps();

    console.log('Geocoding address:', address);

    const geocoder = new google.maps.Geocoder();

    const request: google.maps.GeocoderRequest = {
      address: address,
      componentRestrictions: { country: 'AU' },
    };

    return new Promise((resolve, reject) => {
      geocoder.geocode(request, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
          console.log('Successfully geocoded address');

          const result = results[0];

          // Extract address components
          let locality = '';
          let postcode = '';
          let state = '';

          result.address_components?.forEach((component) => {
            if (component.types.includes('locality')) {
              locality = component.long_name;
            } else if (component.types.includes('postal_code')) {
              postcode = component.long_name;
            } else if (component.types.includes('administrative_area_level_1')) {
              state = component.short_name;
            }
          });

          const details: GoogleAddressDetails = {
            address: result.formatted_address,
            coordinates: {
              lat: result.geometry.location.lat(),
              lng: result.geometry.location.lng(),
            },
            locality,
            postcode,
            state,
          };

          resolve(details);
        } else {
          console.error('Google Geocoding API returned status:', status);
          reject(new Error(`Geocoding error: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error('Google Geocoding error:', error);
    return null;
  }
}
